// Minimal Server-Sent Events parser for streams read with fetch().
//
// EventSource only supports GET (message + history + token end up in the URL),
// so streaming endpoints are consumed with fetch + ReadableStream instead and
// parsed here, following the WHATWG "event stream interpretation" rules:
//  - lines end with \r\n, \n or \r (a \r\n pair may be split across chunks)
//  - an empty line dispatches the pending event
//  - multiple `data:` lines are joined with \n
//  - lines starting with ':' are comments (keepalives) and are ignored
//  - an event with no data lines is not dispatched
//  - an incomplete event at end of stream is discarded

export interface SSEMessage {
  /** Value of the `event:` field, 'message' when absent. */
  event: string
  /** Data lines joined with '\n'. */
  data: string
  /** Last seen `id:` value, if any. */
  id?: string
}

export class SSEParser {
  private buffer = ''
  private dataLines: string[] = []
  private eventType = ''
  private lastEventId: string | undefined

  constructor(private readonly onMessage: (message: SSEMessage) => void) {}

  /** Feed decoded text. May contain any number of partial or complete lines. */
  feed(text: string): void {
    this.buffer += text
    this.processBuffer(false)
  }

  /** Signal end of stream: flushes a trailing lone '\r' line terminator. */
  end(): void {
    this.processBuffer(true)
    // Per spec, an event that was not terminated by a blank line is dropped.
    this.buffer = ''
    this.dataLines = []
    this.eventType = ''
  }

  private processBuffer(final: boolean): void {
    const buf = this.buffer
    let pos = 0
    while (pos < buf.length) {
      const lf = buf.indexOf('\n', pos)
      const cr = buf.indexOf('\r', pos)
      let lineEnd: number
      let next: number
      if (cr !== -1 && (lf === -1 || cr < lf)) {
        // A trailing '\r' may be the first half of a '\r\n' split across chunks.
        if (cr === buf.length - 1 && !final) break
        lineEnd = cr
        next = buf.charCodeAt(cr + 1) === 10 /* \n */ ? cr + 2 : cr + 1
      } else if (lf !== -1) {
        lineEnd = lf
        next = lf + 1
      } else {
        break
      }
      this.processLine(buf.slice(pos, lineEnd))
      pos = next
    }
    this.buffer = buf.slice(pos)
  }

  private processLine(line: string): void {
    if (line === '') {
      this.dispatch()
      return
    }
    if (line.charCodeAt(0) === 58 /* : */) return // comment / keepalive

    const colon = line.indexOf(':')
    let field: string
    let value: string
    if (colon === -1) {
      field = line
      value = ''
    } else {
      field = line.slice(0, colon)
      value = line.slice(colon + 1)
      if (value.charCodeAt(0) === 32 /* space */) value = value.slice(1)
    }

    switch (field) {
      case 'data':
        this.dataLines.push(value)
        break
      case 'event':
        this.eventType = value
        break
      case 'id':
        if (!value.includes('\0')) this.lastEventId = value
        break
      default:
        // 'retry' and unknown fields are ignored.
        break
    }
  }

  private dispatch(): void {
    if (this.dataLines.length === 0) {
      this.eventType = ''
      return
    }
    const message: SSEMessage = {
      event: this.eventType || 'message',
      data: this.dataLines.join('\n'),
      id: this.lastEventId,
    }
    this.dataLines = []
    this.eventType = ''
    this.onMessage(message)
  }
}

/**
 * Byte-level wrapper: decodes UTF-8 incrementally (multibyte characters split
 * across chunks are handled by TextDecoder's streaming mode) and feeds the
 * parser.
 */
export function createSSEDecoder(onMessage: (message: SSEMessage) => void) {
  const decoder = new TextDecoder('utf-8')
  const parser = new SSEParser(onMessage)
  return {
    push(chunk: Uint8Array): void {
      parser.feed(decoder.decode(chunk, { stream: true }))
    },
    end(): void {
      parser.feed(decoder.decode())
      parser.end()
    },
  }
}

/**
 * Read an SSE response body to completion (or until `onMessage` returns true),
 * dispatching every event. Rejects with an AbortError if the request's
 * AbortSignal fires while reading.
 */
export async function readSSEStream(
  body: ReadableStream<Uint8Array>,
  onMessage: (message: SSEMessage) => boolean | void,
): Promise<void> {
  const reader = body.getReader()
  let stop = false
  const sse = createSSEDecoder((message) => {
    if (stop) return
    if (onMessage(message) === true) stop = true
  })
  try {
    while (!stop) {
      const { done, value } = await reader.read()
      if (done) {
        sse.end()
        break
      }
      if (value) sse.push(value)
    }
  } finally {
    if (stop) {
      reader.cancel().catch(() => {})
    } else {
      try { reader.releaseLock() } catch { /* ignore */ }
    }
  }
}
